import React, { useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { useDrag } from "react-dnd";

import GanttFaseBar, { ALTEZZA_LANE_FASE, PADDING_FASE } from "./GanttFaseBar";
import { FRAZIONE_BAR_TYPE, FrazioneBarDragItem } from "./schedulazioneGanttTypes";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import {FaseFrazione, FrazioneOdP} from "../frazioniOdPSlice";
import Button from "@mui/material/Button";

// ============================================
// CONSTANTS
// ============================================

/** Padding sopra/sotto la zona fasi dentro la frazione */
const PADDING_CONTENITORE = 20; // spazio per etichetta in alto

/** Altezza minima della barra frazione */
const ALTEZZA_MINIMA = 40;

// ============================================
// UTILITIES
// ============================================

/**
 * Calcola la lane per ogni fase per evitare sovrapposizioni visive.
 * Algoritmo greedy: assegna ogni fase alla prima lane libera.
 */
const calcolaLanesFasi = (fasi: FaseFrazione[]): Map<number, number> => {
    const laneMap = new Map<number, number>();

    if (!fasi || fasi.length === 0) return laneMap;

    const sorted = [...fasi]
        .filter((f) => f.data_inizio_pianificata && f.data_fine_pianificata)
        .sort((a, b) => {
            const dateA = DateTime.fromISO(a.data_inizio_pianificata as string);
            const dateB = DateTime.fromISO(b.data_inizio_pianificata as string);
            return dateA.toMillis() - dateB.toMillis();
        });

    const lanes: DateTime[] = [];

    sorted.forEach((fase) => {
        const dataInizio = DateTime.fromISO(fase.data_inizio_pianificata as string);
        const dataFine = DateTime.fromISO(fase.data_fine_pianificata as string);

        // Cerca la prima lane dove la fase non si sovrappone
        let laneIndex = lanes.findIndex((laneEndDate) => laneEndDate < dataInizio);

        if (laneIndex === -1) {
            laneIndex = lanes.length;
            lanes.push(dataFine);
        } else {
            lanes[laneIndex] = dataFine;
        }

        laneMap.set(fase.id!, laneIndex);
    });

    return laneMap;
};

/**
 * Calcola il numero massimo di lane occupate
 */
const getMaxLane = (laneMap: Map<number, number>): number => {
    if (laneMap.size === 0) return 0;
    return Math.max(...Array.from(laneMap.values()));
};

// ============================================
// INTERFACES
// ============================================

interface GanttFrazioneBarProps {
    frazione: FrazioneOdP;
    dataInizioGantt: DateTime;
    totaleGiorni: number;
    offsetTop: number;
    onFaseClick?: (faseFrazioneId: number) => void;
    onFrazioneDelete: (frazioneToDelete: FrazioneOdP) => void;
    onFaseDelete: (faseFrazioneToDelete: FaseFrazione) => void;
    onFaseEdit: (faseFrazione: FaseFrazione) => void;
    isAttivo?: boolean;
    onSelect?: (odpId: number | undefined) => void;
    repartiSelezionati?: number[];
    operatoriSelezionati?: number[];
}

// ============================================
// COMPONENT
// ============================================

const GanttFrazioneBar = ({
                              frazione,
                              dataInizioGantt,
                              totaleGiorni,
                              offsetTop,
                              onFaseClick,
                              onFrazioneDelete,
                              onFaseDelete,
                              onFaseEdit,
                              isAttivo,
                              onSelect,
                              repartiSelezionati,
                              operatoriSelezionati,
                          }: GanttFrazioneBarProps) => {
    const theme = useTheme();

    // Lane per le fasi
    const fasi = frazione.fasi || [];
    const laneMap = useMemo(() => calcolaLanesFasi(frazione.fasi || []), [frazione.fasi]);
    const maxLane = useMemo(() => getMaxLane(laneMap), [laneMap]);

    // Drag della frazione intera
    const ref = useRef<HTMLDivElement>(null);
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: FRAZIONE_BAR_TYPE,
            item: {
                type: FRAZIONE_BAR_TYPE,
                frazioneId: frazione.id!,
            } as FrazioneBarDragItem,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [frazione.id]
    );
    drag(ref);

    // Altezza dinamica: etichetta + lanes fasi + padding
    const numLanes = maxLane + 1;
    const altezzaFrazione = Math.max(
        ALTEZZA_MINIMA,
        PADDING_CONTENITORE + numLanes * ALTEZZA_LANE_FASE + PADDING_FASE * 2
    );

    // Date estremi della frazione (calcolate dalle fasi)
    const { frazioneDataDa, frazioneDataA } = useMemo((): {
        frazioneDataDa: DateTime;
        frazioneDataA: DateTime;
    } => {
        const fasiConDate = (frazione.fasi || []).filter(
            (f) => f.data_inizio_pianificata && f.data_fine_pianificata
        );

        if (fasiConDate.length === 0) {
            return { frazioneDataDa: dataInizioGantt, frazioneDataA: dataInizioGantt };
        }

        const dateInizio = fasiConDate.map((f) =>
            DateTime.fromISO(f.data_inizio_pianificata as string)
        );
        const dateFine = fasiConDate.map((f) =>
            DateTime.fromISO(f.data_fine_pianificata as string)
        );

        return {
            frazioneDataDa: DateTime.min(...dateInizio) ?? dataInizioGantt,
            frazioneDataA: DateTime.max(...dateFine) ?? dataInizioGantt,
        };
    }, [frazione.fasi, dataInizioGantt]);

    // Calcola posizione orizzontale della barra frazione nel gantt
    const { left, width, isVisible, clippedLeft, clippedRight, dataInizioEffettiva, dataFineEffettiva } = useMemo(() => {
        const fasiConDate = (frazione.fasi || []).filter(
            (f) => f.data_inizio_pianificata && f.data_fine_pianificata
        );

        if (fasiConDate.length === 0) {
            return { left: "0%", width: "0%", isVisible: false, clippedLeft: false, clippedRight: false, dataInizioEffettiva: dataInizioGantt, dataFineEffettiva: dataInizioGantt };
        }

        // Calcola fine range visibile
        let giorniContati = 0;
        let dataFineRange = dataInizioGantt;
        while (giorniContati < totaleGiorni) {
            if (dataFineRange.weekday <= 5) {
                giorniContati++;
            }
            dataFineRange = dataFineRange.plus({ days: 1 });
        }

        // Fuori range
        if (frazioneDataA < dataInizioGantt || frazioneDataDa > dataFineRange) {
            return { left: "0%", width: "0%", isVisible: false, clippedLeft: false, clippedRight: false, dataInizioEffettiva: dataInizioGantt, dataFineEffettiva: dataInizioGantt };
        }

        // Clamp e traccia i lati tagliati
        const isClippedLeft = frazioneDataDa < dataInizioGantt;
        const isClippedRight = frazioneDataA > dataFineRange;
        const inizioEffettivo =
            isClippedLeft ? dataInizioGantt : frazioneDataDa;
        const fineEffettiva =
            isClippedRight ? dataFineRange : frazioneDataA;

        // Giorni lavorativi dall'inizio
        let giorniDaInizio = 0;
        let giorno = dataInizioGantt;
        while (giorno < inizioEffettivo) {
            if (giorno.weekday <= 5) {
                giorniDaInizio++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        // Durata in giorni lavorativi
        let durataGiorni = 0;
        giorno = inizioEffettivo;
        while (giorno <= fineEffettiva) {
            if (giorno.weekday <= 5) {
                durataGiorni++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        const leftPercent = (giorniDaInizio / totaleGiorni) * 100;
        const widthPercent = (durataGiorni / totaleGiorni) * 100;

        return {
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 2)}%`,
            isVisible: true,
            clippedLeft: isClippedLeft,
            clippedRight: isClippedRight,
            dataInizioEffettiva: inizioEffettivo,
            dataFineEffettiva: fineEffettiva,
        };
    }, [frazioneDataDa, frazioneDataA, frazione.fasi, dataInizioGantt, totaleGiorni]);

    // Popover (hooks devono stare PRIMA di ogni early return)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverOpen = Boolean(anchorEl);

    const handleBarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        onSelect?.(isAttivo ? undefined : frazione.odp);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    if (!isVisible) {
        return null;
    }

    const popoverContent = (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold">
                {frazione.codice_articolo}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
                {frazione.odp_display || `OdP #${frazione.odp}`} — Fr.{frazione.numero_frazione}
            </Typography>
            <Typography variant="caption" display="block">
                Fasi: {fasi.length}
            </Typography>
            {frazione.ha_ostacoli_attivi && (
                <Typography variant="caption" display="block" color="error">
                    ⚠ Ostacoli attivi
                </Typography>
            )}
            <Button variant="contained" sx={{mt:2}} color='warning' onClick={()=>onFrazioneDelete(frazione)}>Elimina Frazione</Button>
        </Box>
    );

    return (
        <>
            <Box
                ref={ref}

                sx={{
                    position: "absolute",
                    top: offsetTop,
                    left,
                    width,
                    height: altezzaFrazione,
                    backgroundColor: isAttivo
                        ? theme.palette.primary.main + "20"
                        : theme.palette.grey[200] + "80",
                    border: isAttivo
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: 1,
                    // Lati tagliati: rimuovi bordo e border-radius
                    ...(clippedLeft && {
                        borderLeft: "none",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                    }),
                    ...(clippedRight && {
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    }),
                    pointerEvents: "auto",
                    overflow: "visible",
                    cursor: "grab",
                    opacity: isDragging ? 0.4 : 1,
                    transition: "border-color 0.2s ease-in-out, background-color 0.2s ease-in-out, opacity 0.2s ease-in-out",
                    ...(frazione.ha_ostacoli_attivi && !isAttivo && {
                        borderColor: theme.palette.error.light,
                        borderStyle: "dashed",
                    }),
                }}
            >
                {/* Etichetta frazione */}
                <Box
                    onClick={handleBarClick}
                    sx={{
                        height: PADDING_CONTENITORE,
                        display: "flex",
                        alignItems: "center",
                        px: 0.5,
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            fontWeight: "bold",
                            fontSize: "0.6rem",
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                        }}
                    >
                        {frazione.codice_articolo || `OdP ${frazione.odp}`} Fr.{frazione.numero_frazione}
                    </Typography>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{
                            ml: 1,
                            fontSize: "0.6rem",
                            color: theme.palette.text.secondary,
                            flexShrink: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {frazione.odp_display || `OdP #${frazione.odp}`} — Fr.{frazione.numero_frazione}
                    </Typography>
                </Box>

                {/* Area fasi (posizionamento relativo per le barre) */}
                <Box
                    sx={{
                        position: "relative",
                        height: altezzaFrazione - PADDING_CONTENITORE,
                    }}
                >
                    {fasi
                        .filter(
                            (f) =>
                                f.data_inizio_pianificata &&
                                f.data_fine_pianificata
                        )
                        .map((fase) => (
                            <GanttFaseBar
                                key={fase.id}
                                fase={fase}
                                frazioneId={frazione.id!}
                                dataInizioFrazione={dataInizioEffettiva}
                                dataFineFrazione={dataFineEffettiva}
                                lane={laneMap.get(fase.id!) || 0}
                                onClick={onFaseClick}
                                onFaseDelete={onFaseDelete}
                                onFaseEdit={onFaseEdit}
                                repartiSelezionati={repartiSelezionati}
                                operatoriSelezionati={operatoriSelezionati}
                            />
                        ))}
                </Box>
            </Box>
            <Popover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {popoverContent}
            </Popover>
        </>
    );
};

export default GanttFrazioneBar;

// Export altezza per uso nel componente timeline
export { PADDING_CONTENITORE };
export const calcolaAltezzaFrazione = (fasi: FaseFrazione[]): number => {
    const laneMap = calcolaLanesFasi(fasi);
    const maxLane = getMaxLane(laneMap);
    const numLanes = maxLane + 1;
    return Math.max(
        ALTEZZA_MINIMA,
        PADDING_CONTENITORE + numLanes * ALTEZZA_LANE_FASE + PADDING_FASE * 2
    );
};